import { ThemeEvents, VariantUpdateEvent, CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {{ id: number; price: number; compare_at_price: number | null; available: boolean; title?: string; image?: string; color?: string | null }} DrVariant
 * @typedef {{ productId: number; handle?: string; title?: string; image?: string; productColor?: string; variants: DrVariant[] }} DrSecondaryPick
 * @typedef {{ discountSecondPercent: number; moneyFormat: string; variants: DrVariant[]; initialVariantId: number; secondaryPicks?: DrSecondaryPick[]; giftProducts?: DrSecondaryPick[]; giftProduct?: DrSecondaryPick | null }} DrConfig
 */

class DoreBundleBlock extends HTMLElement {
  /** @type {DrConfig | null} */
  #cfg = null;

  /** @type {number} */
  #vid = 0;

  /** @type {'single' | 'double'} */
  #mode = 'double';

  /** Tweede-masker-keuze (index in secondaryPicks) */
  /** @type {number} */
  #secondaryPickIx = 0;

  /** Variant-id van gekozen tweede masker-product */
  /** @type {number} */
  #secondaryVid = 0;

  /** Gratis tank-keuze (index in secondaryPicks) */
  /** @type {number} */
  #freePickIx = 0;

  /** Variant-id van gekozen gratis tank */
  /** @type {number} */
  #freeVid = 0;

  /** @type {boolean} */
  #hasSecondProductSelected = false;

  /** @type {boolean} */
  #hasFreeTankSelected = false;

  /** @type {'second' | 'free'} */
  #pickerTarget = 'second';

  /** @type {'hidden' | 'locked' | 'unlocked'} */
  #lastGiftsState = 'hidden';

  /** @type {boolean} */
  #mixedCartBusy = false;

  /** @type {string} */
  #lastLoggedFreeTankKey = '';

  /** @type {AbortController | undefined} */
  #abort;

  /** @returns {DrConfig} */
  #requireCfg() {
    if (!this.#cfg) throw new Error('dore bundle: missing config');
    return this.#cfg;
  }

  /** @returns {DrVariant | undefined} */
  #variant() {
    return this.#requireCfg().variants.find((x) => Number(x.id) === Number(this.#vid));
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const format = this.#requireCfg().moneyFormat || '{{amount}}';

    /** @type {any} */
    const S = typeof window !== 'undefined' ? window.Shopify : undefined;
    if (S?.formatMoney) {
      return this.#normalizeMoney(S.formatMoney(cents, format));
    }

    try {
      return this.#normalizeMoney(new Intl.NumberFormat(this.dataset.moneyLocale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100));
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeMoney(value) {
    return String(value).replace(/(\d(?:[.\s]\d{3})*),(\d{2})(?!\d)/g, (_match, amount, cents) => {
      return `${String(amount).replace(/[.\s]/g, ',')}.${cents}`;
    });
  }

  /**
   * @param {DrVariant} v
   */
  #refCents(v) {
    const p = Number(v.price) || 0;
    const c = v.compare_at_price != null ? Number(v.compare_at_price) : null;
    return c != null && c > p ? c : p;
  }

  /**
   * @param {number} priceCents
   * @param {number} pct
   */
  #disc(priceCents, pct) {
    return Math.round((priceCents * (100 - pct)) / 100);
  }

  /** @returns {boolean} */
  #hasSecondaryPicks() {
    return (this.#cfg?.secondaryPicks?.length ?? 0) > 0;
  }

  /** @returns {boolean} */
  #hasGiftProduct() {
    return this.#giftProducts().some((pick) => (pick.variants?.length ?? 0) > 0);
  }

  /** @returns {DrSecondaryPick[]} */
  #giftProducts() {
    const picks = this.#cfg?.giftProducts;
    if (Array.isArray(picks) && picks.length) return picks;
    return this.#cfg?.giftProduct ? [this.#cfg.giftProduct] : [];
  }

  /** @returns {DrSecondaryPick | undefined} */
  #giftPick() {
    const picks = this.#giftProducts();
    if (!picks.length) return undefined;
    if (this.#freePickIx < 0 || this.#freePickIx >= picks.length) this.#freePickIx = 0;
    return picks[this.#freePickIx];
  }

  /**
   * @param {string | undefined} src
   * @returns {string}
   */
  #normalizeImageUrl(src) {
    const value = (src || '').trim();
    if (!value) return '';
    return value.startsWith('//') ? `https:${value}` : value;
  }

  /**
   * @param {Element | null} target
   * @param {string} src
   * @param {string} alt
   * @returns {HTMLImageElement | null}
   */
  #setImageSource(target, src, alt) {
    const nextSrc = this.#normalizeImageUrl(src);
    if (!target || !nextSrc) return null;

    /** @type {HTMLImageElement} */
    let img;
    if (target instanceof HTMLImageElement) {
      img = target;
    } else {
      img = document.createElement('img');
      img.className = target.className;
      target.replaceWith(img);
    }

    const picture = img.closest('picture');
    picture?.querySelectorAll('source').forEach((source) => {
      source.removeAttribute('srcset');
      source.removeAttribute('sizes');
    });

    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = nextSrc;
    img.alt = alt;
    return img;
  }

  /**
   * @param {DrSecondaryPick} pick
   * @param {DrVariant} variant
   * @param {string} imageUrl
   */
  #logFreeTankSelection(pick, variant, imageUrl) {
    const key = `${variant.id}|${pick.handle || ''}|${imageUrl}`;
    if (key === this.#lastLoggedFreeTankKey) return;
    this.#lastLoggedFreeTankKey = key;

    console.log('[DoreBundle] Free tank selected', {
      variantId: Number(variant.id),
      giftProductHandle: pick.handle || '',
      imageUrl,
    });
  }

  /** 2-pack: tweede een apart product uit theme-keuzes → aparte cart lines + JSON `/cart/add` */
  /** @returns {boolean} */
  #usesMixedDouble() {
    return this.#mode === 'double' && this.#hasSecondaryPicks();
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #normalizedLabel(value) {
    const raw = value == null ? '' : String(value).trim();
    if (!raw || /default title/i.test(raw)) return '';
    return raw;
  }

  /**
   * @param {DrVariant | undefined} variant
   * @param {string} [productTitle]
   * @returns {string}
   */
  #variantDisplayLabel(variant, productTitle = '') {
    const base = this.#normalizedLabel(productTitle);
    const color = this.#normalizedLabel(variant?.color) || this.#normalizedLabel(variant?.title);
    if (base && color) return `${base} - ${color}`;
    return base || color || '';
  }

  /**
   * @param {DrSecondaryPick | undefined} pick
   * @param {DrVariant | undefined} variant
   * @returns {string}
   */
  #giftDisplayLabel(pick, variant) {
    const base = this.#normalizedLabel(pick?.title).replace(/\s+[-–]\s+FREE Gift$/i, '').trim();
    const variantTitle = this.#normalizedLabel(variant?.title);
    if (base && variantTitle) return `${base} - ${variantTitle}`;
    return base || variantTitle || '';
  }

  /**
   * @param {number} domPickIndex `data-pick-index` / radio value in the modal
   * @returns {number} index in `secondaryPicks`
   */
  #secondaryPickIndexFromDom(domPickIndex) {
    const nPicks = this.#cfg?.secondaryPicks?.length ?? 0;
    if (nPicks < 1) return 0;

    /** @type {HTMLInputElement | null} */
    const radio = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`.dr-bundle__pick-radio[value="${domPickIndex}"]`)
    );
    const select = /** @type {HTMLSelectElement | null} */ (
      this.querySelector(`select.dr-bundle__secondary-variant-select[data-dr-pick-index="${domPickIndex}"]`)
    );
    if (!radio && !select) return Math.max(0, Math.min(domPickIndex, nPicks - 1));

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('.dr-bundle__pick-radio'));
    let arrayIx = 0;
    for (const r of radios) {
      const pickDomIx = Number.parseInt(String(r.value), 10);
      if (Number.isNaN(pickDomIx)) continue;
      if (pickDomIx === domPickIndex) return Math.max(0, Math.min(arrayIx, nPicks - 1));
      arrayIx += 1;
    }

    return Math.max(0, Math.min(domPickIndex, nPicks - 1));
  }

  /**
   * @param {number} domPickIndex
   * @returns {void}
   */
  #applySecondaryPick(domPickIndex) {
    if (!this.#hasSecondaryPicks()) return;

    const arrayIx = this.#secondaryPickIndexFromDom(domPickIndex);
    const pick = this.#cfg?.secondaryPicks?.[arrayIx];
    if (!pick) return;

    this.querySelectorAll('.dr-bundle__pick-radio').forEach((/** @type {HTMLInputElement} */ radio) => {
      radio.checked = Number.parseInt(String(radio.value), 10) === domPickIndex;
    });

    this.#secondaryPickIx = arrayIx;

    /** @type {HTMLSelectElement | null} */
    const sel = /** @type {HTMLSelectElement | null} */ (
      this.querySelector(`select.dr-bundle__secondary-variant-select[data-dr-pick-index="${domPickIndex}"]`)
    );

    let vid = sel?.value ? Number.parseInt(String(sel.value), 10) : 0;
    const fallback = pick.variants.find((x) => x.available) ?? pick.variants[0];
    if (!vid && fallback?.id) vid = Number(fallback.id);
    if (vid > 0) {
      this.#secondaryVid = vid;
      if (sel) sel.value = String(vid);
    }
  }

  /**
   * @param {number} domPickIndex
   * @returns {void}
   */
  #applyFreePick(domPickIndex = this.#freePickIx) {
    if (!this.#hasGiftProduct()) return;

    const picks = this.#giftProducts();
    const pickIx = Math.max(0, Math.min(Number(domPickIndex) || 0, picks.length - 1));
    const pick = picks[pickIx];
    if (!pick) return;

    this.#freePickIx = pickIx;

    /** @type {HTMLSelectElement | null} */
    const sel = /** @type {HTMLSelectElement | null} */ (
      this.querySelector(`select[data-dr-free-variant-select][data-dr-free-pick-index="${pickIx}"]`)
    );

    let vid = sel?.value ? Number.parseInt(String(sel.value), 10) : 0;
    const fallback = pick.variants.find((x) => x.available) ?? pick.variants[0];
    if (!vid && fallback?.id) vid = Number(fallback.id);
    if (vid > 0) {
      this.#freeVid = vid;
      if (sel) sel.value = String(vid);
    }
  }

  /** @returns {void} */
  #syncSecondPickFromDom() {
    if (!this.#hasSecondaryPicks()) return;
    if (this.#pickerTarget !== 'second' && this.#hasSecondProductSelected) return;

    /** @type {HTMLInputElement | null} */
    const r = /** @type {HTMLInputElement | null} */ (this.querySelector('.dr-bundle__pick-radio:checked'));
    const domPickIndex =
      r?.value !== undefined && String(r.value) !== '' ? Number.parseInt(String(r.value), 10) || 0 : 0;

    this.#applySecondaryPick(domPickIndex);
  }

  /** @returns {void} */
  #syncActivePickFromDom() {
    if (this.#pickerTarget === 'free') {
      this.#applyFreePick();
      return;
    }

    this.#syncSecondPickFromDom();
  }

  /**
   * @returns {DrVariant | undefined}
   */
  #secondChosenVariant() {
    /** @type {DrSecondaryPick | undefined} */
    const pick = this.#cfg?.secondaryPicks?.[this.#secondaryPickIx];
    if (!pick) return undefined;
    const bySel = pick.variants.find((x) => Number(x.id) === Number(this.#secondaryVid));
    if (bySel) return bySel;
    const fallback = pick.variants.find((x) => x.available) ?? pick.variants[0];
    if (fallback?.id) this.#secondaryVid = Number(fallback.id);
    return fallback;
  }

  /**
   * @returns {DrVariant | undefined}
   */
  #freeChosenVariant() {
    /** @type {DrSecondaryPick | undefined} */
    const pick = this.#giftPick();
    if (!pick) return undefined;
    const bySel = pick.variants.find((x) => Number(x.id) === Number(this.#freeVid));
    if (bySel) return bySel;
    const fallback = pick.variants.find((x) => x.available) ?? pick.variants[0];
    if (fallback?.id) this.#freeVid = Number(fallback.id);
    return fallback;
  }

  /**
   * Horizon product form in the same section (native Add to cart).
   * @returns {HTMLElement | null}
   */
  #getProductFormRoot() {
    const section = this.closest('.shopify-section');
    if (!section) return null;
    const pid = this.dataset.productId;
    if (pid) {
      const byId = section.querySelector(`product-form-component[data-product-id="${CSS.escape(String(pid))}"]`);
      if (byId) return /** @type {HTMLElement} */ (byId);
    }
    return /** @type {HTMLElement | null} */ (section.querySelector('product-form-component'));
  }

  /**
   * Horizon registreert formulier-submit op document (capture); daardoor draait `handleSubmit`
   * vóór elke downstream listener die op het form zelf wordt geplaatst. Daarop syncen op `window` (capture).
   *
   * @param {SubmitEvent} e
   */
  #onWindowSubmitCapture = (e) => {
    if (String(this.dataset.syncForm || 'true') !== 'true') return;

    /** @type {EventTarget | null} */
    const raw = e.target;

    const form =
      raw instanceof HTMLFormElement
        ? raw
        : raw instanceof HTMLElement
          ? /** @type {HTMLFormElement | null} */ (raw.closest?.('form'))
          : null;
    if (!form || !(form instanceof HTMLFormElement)) return;

    const isAtc =
      form.getAttribute('data-type') === 'add-to-cart-form' || /\bcart\/add\b/.test(String(form.getAttribute('action') || ''));

    if (!isAtc) return;

    const pfRoot = /** @type {HTMLElement | null} */ (form.closest('product-form-component'));
    const expectedPf = this.#getProductFormRoot();

    if (!pfRoot || !expectedPf || pfRoot !== expectedPf) return;

    this.#syncSecondPickFromDom();

    if (this.#usesMixedDouble()) {
      /** @type {any} */
      const W = typeof window !== 'undefined' ? window : undefined;
      const cartUrl = W?.Theme?.routes?.cart_add_url || '/cart/add.js';

      if (!this.#hasSecondProductSelected || !this.#hasFreeTankSelected) {
        const formId = form.getAttribute('id') || '';
        const msg = !this.#hasSecondProductSelected
          ? 'Kies je tweede product om verder te gaan.'
          : 'Kies je gratis tank om verder te gaan.';
        document.dispatchEvent(
          new CartErrorEvent(formId, msg, {}, {})
        );
        const dlg = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-dr-second-dialog]'));
        if (dlg && typeof dlg.showModal === 'function') {
          dlg.setAttribute('aria-hidden', 'false');
          dlg.showModal();
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      const mainInput = /** @type {HTMLInputElement | null} */ (pfRoot.querySelector('input[name="id"]'));
      const mainVid = mainInput?.value ? Number.parseInt(String(mainInput.value), 10) : 0;
      const primary = this.#variant();
      const sv = this.#secondChosenVariant();
      const fv = this.#freeChosenVariant();

      if (!mainVid || !sv?.id || !fv?.id || !primary?.id) {
        const formId = form.getAttribute('id') || '';
        document.dispatchEvent(
          new CartErrorEvent(
            formId,
            'Bundel: productkeuze niet gevonden. Open “Tank kiezen” en kies beide varianten.',
            {},
            {}
          )
        );
        const dlg = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-dr-second-dialog]'));
        if (dlg && typeof dlg.showModal === 'function') dlg.showModal();
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (!primary.available || !sv.available || !fv.available) {
        const formId = form.getAttribute('id') || '';
        const msg = W?.Theme?.translations?.sold_out ?? 'Niet beschikbaar';
        document.dispatchEvent(new CartErrorEvent(formId, String(msg), {}, {}));
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (typeof e.preventDefault === 'function') e.preventDefault();
      e.stopPropagation();
      void this.#addMixedBundleToCart(form, pfRoot, cartUrl);
      return;
    }

    this.#applyBundleToForm(form, pfRoot);
  };

  /**
   * Zet `quantity` + line item property zodat Add to cart 1 of 2 stuks + bundelhints stuurt.
   *
   * `properties[_dore_bundle]` is een hint voor Shopify-kortingen (Functions / apps); begint met _ zodat het vaak verborgen blijft in de mand-UI.
   *
   * @param {HTMLFormElement} form
   * @param {HTMLElement} pf
   * @returns {void}
   */
  #applyBundleToForm(form, pf) {
    const mixed = this.#mode === 'double' && this.#hasSecondaryPicks();
    const desiredQty = this.#mode === 'double' ? 2 : 1;

    /** @type {HTMLElement | null} */
    const qs = /** @type {HTMLElement | null} */ (pf.querySelector('quantity-selector-component'));

    /** @type {HTMLInputElement | null} */
    let qtyInput = null;
    /** @type {any} */
    let qsComp = null;

    if (qs) {
      qsComp = qs;
      qtyInput =
        /** @type {HTMLInputElement | null} */ (qsComp?.refs?.quantityInput) ||
        /** @type {HTMLInputElement | null} */ (qs.querySelector('input[name="quantity"]'));
    }

    const useSelector = qtyInput instanceof HTMLInputElement && !qtyInput.disabled;

    /** Verwijder sync-hidden zodra we weer een actieve qty-selector gebruiken — nooit twee `quantity` submits. */
    form.querySelectorAll('input[name="quantity"][data-dr-bundle-sync]').forEach((n) => n.remove());

    if (useSelector) {
      const input = /** @type {HTMLInputElement} */ (qtyInput);
      const min = parseInt(input.getAttribute('min') || '1', 10) || 1;
      let step = parseInt(input.getAttribute('step') || '1', 10) || 1;
      if (step < 1) step = 1;

      const maxAttr = input.getAttribute('max');
      const max = maxAttr != null && maxAttr !== '' ? parseInt(maxAttr, 10) : null;
      const cartQty = parseInt(input.getAttribute('data-cart-quantity') || '0', 10) || 0;

      let effectiveMax = max;
      if (effectiveMax != null && Number.isFinite(effectiveMax)) {
        effectiveMax = Math.max(effectiveMax - cartQty, min);
      }

      let v = Math.max(desiredQty, min);
      if (effectiveMax != null && Number.isFinite(effectiveMax)) {
        v = Math.min(v, effectiveMax);
      }

      const rem = (v - min) % step;
      if (rem !== 0) v -= rem;
      if (v < min) v = min;

      input.value = String(v);

      if (typeof qsComp.onQuantityChange === 'function') qsComp.onQuantityChange();
      else input.dispatchEvent(new Event('change', { bubbles: true }));

      if (typeof qsComp.updateButtonStates === 'function') qsComp.updateButtonStates();
    } else {
      /** Quantity-block uitgeschakeld of selector disabled → één verborgen `quantity`-veld. */
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'quantity';
      hidden.setAttribute('data-dr-bundle-sync', '1');
      hidden.value = String(desiredQty);
      form.appendChild(hidden);
    }

    form.querySelectorAll('input[data-dr-bundle-line-prop]').forEach((n) => n.remove());
    const modeVal =
      this.#mode !== 'double' ? '1-pack' : mixed ? '2-pack-mixed' : '2-pack';

    /** @type {HTMLInputElement} */
    const propIn = document.createElement('input');
    propIn.type = 'hidden';
    propIn.setAttribute('data-dr-bundle-line-prop', '1');
    propIn.name = 'properties[_dore_bundle]';
    propIn.value = modeVal;
    form.appendChild(propIn);

    /** @type {HTMLInputElement} */
    const offerIn = document.createElement('input');
    offerIn.type = 'hidden';
    offerIn.setAttribute('data-dr-bundle-line-prop', '1');
    offerIn.name = 'properties[_dore_bundle_offer]';
    offerIn.value = this.#mode === 'double' ? '2-plus-1-free' : '1-pack';
    form.appendChild(offerIn);
  }

  /**
   * Twee aparte variants toevoegen (`/cart/add.js` JSON) — voorkomt samenvoegen tot qty 2 op één regel bij gemengde producten.
   *
   * @param {HTMLFormElement} form
   * @param {HTMLElement} pf
   * @param {string} cartUrl
   */
  async #addMixedBundleToCart(form, pf, cartUrl) {
    /** @type {any} */
    const Theme = typeof window !== 'undefined' ? /** @type {any} */ (window).Theme : undefined;

    if (this.#mixedCartBusy) return;

    this.#syncSecondPickFromDom();

    const mainInput = /** @type {HTMLInputElement | null} */ (pf.querySelector('input[name="id"]'));
    const mainVid = mainInput?.value ? Number.parseInt(String(mainInput.value), 10) : 0;
    const sv = this.#secondChosenVariant();
    const fv = this.#freeChosenVariant();

    /** @type {DrVariant | undefined} */
    const primary = this.#variant();

    if (!mainVid || !sv?.id || !fv?.id || !primary?.id) return;

    const formSourceId = form.getAttribute('id') || '';

    if (!primary.available || !sv.available || !fv.available) {
      const msg = Theme?.translations?.sold_out ?? 'Unavailable';
      document.dispatchEvent(new CartErrorEvent(formSourceId || 'dore-bundle', String(msg), {}, {}));

      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });

    const p1 = { _dore_bundle: '2-pack-mixed', _dore_bundle_offer: '2-plus-1-free', _dore_bundle_slot: '1' };
    const p2 = { _dore_bundle: '2-pack-mixed', _dore_bundle_offer: '2-plus-1-free', _dore_bundle_slot: '2' };
    const p3 = {
      _dore_bundle: '2-pack-mixed',
      _dore_bundle_offer: '2-plus-1-free',
      _dore_bundle_slot: 'free',
      _bundle: 'free_tank',
    };

    /** @type {any} */
    const bodyPayload = {
      items: [
        { id: mainVid, quantity: 1, properties: { ...p1 } },
        { id: Number(sv.id), quantity: 1, properties: { ...p2 } },
        { id: Number(fv.id), quantity: 1, properties: { ...p3 } },
      ],
    };

    if (sectionIds.length) {
      bodyPayload.sections = sectionIds.join(',');

      bodyPayload.sections_url = window.location.pathname;
    }

    const bodyJson = JSON.stringify(bodyPayload);

    /** @type {NodeListOf<HTMLElement>} */
    const atcBoxes = pf.querySelectorAll('add-to-cart-component');

    atcBoxes.forEach((/** @type {HTMLElement} */ node) => {
      /** @type {any} */
      const c = node;

      if (typeof c.disable === 'function') c.disable();


    });


    this.#mixedCartBusy = true;

    try {

      const url = cartUrl || Theme?.routes?.cart_add_url || '/cart/add.js';
      const resp = await fetch(url, fetchConfig('json', { body: bodyJson }));


      /** @type {any} */

      let data;

      try {
        data = await resp.json();


      } catch {
        document.dispatchEvent(new CartErrorEvent(formSourceId || '', 'Cart', {}, {}));


        return;
      }

      const hasErrorPayload = Boolean(data?.status);


      const hasNestedErrors =
        data?.errors &&
        typeof data.errors === 'object' &&
        !Array.isArray(data.errors) &&

        Object.keys(data.errors).length > 0;

      if (!resp.ok || hasErrorPayload || hasNestedErrors) {
        document.dispatchEvent(

          new CartErrorEvent(formSourceId, String(data.message || resp.statusText || 'Cart'), data.description ?? {}, data.errors ?? {})


        );


        document.dispatchEvent(

          new CartAddEvent({}, String(pf.id || ''), {
            didError: true,

            source: 'product-form-component',

            itemCount: 0,

            productId: pf.dataset.productId,
          })

        );


        return;
      }

      document.dispatchEvent(


        new CartAddEvent({}, String(pf.id || form.id || ''), {
          source: 'product-form-component',

          itemCount: 3,

          productId: pf.dataset.productId,

          variantId: String(mainVid),

          sections: data?.sections,

        })

      );


    } finally {


      atcBoxes.forEach((/** @type {HTMLElement} */ node) => {
        /** @type {any} */

        const c = node;



        if (typeof c.enable === 'function') c.enable();


      });

      this.#mixedCartBusy = false;


    }

  }




  /**

   * @returns {void}
   **/

  #syncWithProductForm() {
    if (String(this.dataset.syncForm || 'true') !== 'true') return;

    const pf = this.#getProductFormRoot();
    if (!pf) return;

    /** @type {HTMLFormElement | null} */
    const form = /** @type {HTMLFormElement | null} */ (
      pf.querySelector('form[data-type="add-to-cart-form"]') || pf.querySelector('form[action*="/cart/add"]')
    );
    if (!form) return;

    this.#applyBundleToForm(form, pf);
  }

  /** @returns {Promise<void>} */
  async #hydrateFreeTankFromCart() {
    if (!this.#hasGiftProduct()) return;

    try {
      const resp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!resp.ok) return;

      /** @type {{ items?: Array<{ variant_id?: number; properties?: Record<string, string> }> }} */
      const cart = await resp.json();
      const line = cart.items?.find((item) => item.properties?._bundle === 'free_tank');
      const variantId = Number(line?.variant_id || 0);
      if (!variantId) return;

      const picks = this.#giftProducts();
      for (let pickIx = 0; pickIx < picks.length; pickIx += 1) {
        const pick = picks[pickIx];
        if (!pick?.variants?.length) continue;

        const variant = pick.variants.find((x) => Number(x.id) === variantId);
        if (!variant) continue;

        this.#freePickIx = pickIx;
        this.#freeVid = variantId;
        this.#hasFreeTankSelected = true;

        const sel = /** @type {HTMLSelectElement | null} */ (
          this.querySelector(`select[data-dr-free-variant-select][data-dr-free-pick-index="${pickIx}"]`)
        );
        if (sel) sel.value = String(variantId);

        this.#paint();
        this.#syncWithProductForm();
        return;
      }
    } catch {
      // Cart hydration is best-effort; the picker still works from its embedded gift data.
    }
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    window.addEventListener('submit', this.#onWindowSubmitCapture, { capture: true, signal });

    const txt = /** @type {HTMLScriptElement | null} */ (this.querySelector('script[data-dr-config]'))?.textContent;
    if (!txt) return;

    try {
      this.#cfg = /** @type {DrConfig} */ (JSON.parse(txt));
    } catch {
      return;
    }

    this.#vid = this.#cfg.initialVariantId;

    const pf = this.#getProductFormRoot();
    const idInput = /** @type {HTMLInputElement | null} */ (pf?.querySelector('input[name="id"]'));
    const formVid = idInput?.value ? Number.parseInt(String(idInput.value), 10) : 0;
    if (formVid > 0 && this.#cfg.variants.some((x) => Number(x.id) === formVid)) {
      this.#vid = formVid;
    }

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('.dr-bundle__radios'));
    radios.forEach((r) => r.addEventListener('change', () => this.#syncFromRadios(), { signal }));

    this.querySelectorAll('.js-dr-opt').forEach((lab) => {
      if (/** @type {HTMLElement} */ (lab).getAttribute('aria-disabled') === 'true') return;
      lab.addEventListener(
        'keydown',
        /** @param {KeyboardEvent} e */ (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            /** @type {HTMLElement} */ (lab).click();
          }
        },
        { signal }
      );
    });

    const choose = this.querySelector('[data-dr-placeholder-choose]');
    choose?.addEventListener('click', (e) => e.preventDefault(), { signal });

    this.#bindSecondModal(signal);

    const section = this.closest('.shopify-section') ?? document;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariant, { signal });

    this.#bindSecondaryPicks(signal);
    this.#syncSecondPickFromDom();

    this.#syncFromRadios();
    void this.#hydrateFreeTankFromCart();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {Event} e */
  #onVariant = (e) => {
    const ev = /** @type {VariantUpdateEvent} */ (e);
    if (!(ev instanceof VariantUpdateEvent)) return;
    if (ev.detail.data.productId !== (this.dataset.productId || '')) return;
    const res = ev.detail.resource;
    if (!res || res.id == null) return;
    this.#vid = Number(res.id);
    requestAnimationFrame(() => {
      this.#paint();
      this.#syncWithProductForm();
    });
  };

  /**
   * @param {AbortSignal} signal
   */
  #bindSecondModal(signal) {
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-dr-second-dialog]'));
    if (!dialog) return;

    this.querySelectorAll('[data-dr-open-second-modal]').forEach((btn) => {
      btn.addEventListener(
        'click',
        /** @param {Event} e */ (e) => {
          e.preventDefault();
          e.stopPropagation();
          const target = /** @type {HTMLElement} */ (e.currentTarget);
          this.#pickerTarget = target.dataset.drPickerTarget === 'free' ? 'free' : 'second';
          this.#primeModalForTarget();
          dialog.setAttribute('aria-hidden', 'false');
          if (typeof dialog.showModal === 'function') dialog.showModal();
        },
        { signal }
      );
    });

    this.querySelectorAll('[data-dr-close-second-modal]').forEach((btn) => {
      btn.addEventListener(
        'click',
        /** @param {Event} e */ (e) => {
          e.preventDefault();
          e.stopPropagation();
          dialog.setAttribute('aria-hidden', 'true');
          dialog.close();
        },
        { signal }
      );
    });

    dialog.addEventListener(
      'click',
      /** @param {MouseEvent} e */ (e) => {
        if (e.target === dialog) {
          dialog.setAttribute('aria-hidden', 'true');
          dialog.close();
        }
      },
      { signal }
    );

    dialog.addEventListener(
      'cancel',
      () => {
        dialog.setAttribute('aria-hidden', 'true');
      },
      { signal }
    );

    dialog.addEventListener(
      'close',
      () => {
        dialog.setAttribute('aria-hidden', 'true');
      },
      { signal }
    );
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindSecondaryPicks(signal) {
    if (!this.querySelector('[data-dr-secondary-picks]')) return;

    this.querySelectorAll('.dr-bundle__pick-radio').forEach((/** @type {HTMLInputElement} */ radio) => {
      radio.addEventListener('change', () => this.#secondaryPickChanged(), { signal });
    });

    this.querySelectorAll('select.dr-bundle__secondary-variant-select').forEach((/** @type {HTMLSelectElement} */ sel) => {
      sel.addEventListener('change', () => this.#secondaryPickChanged(), { signal });
    });

    this.querySelectorAll('select[data-dr-free-variant-select]').forEach((/** @type {HTMLSelectElement} */ sel) => {
      sel.addEventListener('change', () => {
        if (this.#pickerTarget !== 'free') return;
        this.#freePickIx = Number.parseInt(String(sel.dataset.drFreePickIndex || '0'), 10) || 0;
        this.#secondaryPickChanged();
      }, { signal });
    });

    this.querySelectorAll('[data-dr-modal-choose]').forEach((btn) => {
      btn.addEventListener(
        'click',
        /** @param {Event} e */ (e) => {
          e.preventDefault();
          e.stopPropagation();

          const target = /** @type {HTMLElement} */ (e.currentTarget);
          const domPickIndex = Number.parseInt(String(target.dataset.pickIndex || '0'), 10) || 0;

          if (this.#pickerTarget === 'free') {
            this.#applyFreePick(domPickIndex);
            this.#hasFreeTankSelected = true;
          } else {
            this.#applySecondaryPick(domPickIndex);
            this.#hasSecondProductSelected = true;
          }
          this.#secondaryPickChanged();

          const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-dr-second-dialog]'));
          if (dialog) {
            dialog.setAttribute('aria-hidden', 'true');
            dialog.close();
          }
        },
        { signal }
      );
    });

    this.querySelectorAll('[data-dr-free-modal-choose]').forEach((btn) => {
      btn.addEventListener(
        'click',
        /** @param {Event} e */ (e) => {
          e.preventDefault();
          e.stopPropagation();

          const target = /** @type {HTMLElement} */ (e.currentTarget);
          const pickIx = Number.parseInt(String(target.dataset.freePickIndex || '0'), 10) || 0;

          this.#pickerTarget = 'free';
          this.#applyFreePick(pickIx);
          this.#hasFreeTankSelected = true;
          this.#secondaryPickChanged();

          const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-dr-second-dialog]'));
          if (dialog) {
            dialog.setAttribute('aria-hidden', 'true');
            dialog.close();
          }
        },
        { signal }
      );
    });

    this.querySelectorAll('[data-dr-remove-second-product]').forEach((btn) => {
      btn.addEventListener(
        'click',
        /** @param {Event} e */ (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.#clearSecondSelectedProduct();
          this.#secondaryPickChanged();
        },
        { signal }
      );
    });
  }

  /** @returns {void} */
  #secondaryPickChanged() {
    if (!this.#cfg) return;
    this.#syncActivePickFromDom();
    this.#paint();
    this.#syncWithProductForm();
  }

  /** @returns {void} */
  #clearSecondSelectedProduct() {
    this.#hasSecondProductSelected = false;
    this.#secondaryVid = 0;

    this.classList.remove('dr-bundle--has-second-product');

    const img = /** @type {HTMLImageElement | null} */ (this.querySelector('.dr-bundle__second-selected-image'));
    const title = /** @type {HTMLElement | null} */ (this.querySelector('.dr-bundle__second-selected-title'));
    const removeBtn = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-remove-second-product]'));
    if (img) img.removeAttribute('src');
    if (title) title.textContent = '';
    if (removeBtn) removeBtn.setAttribute('hidden', '');
  }

  /** @returns {void} */
  #clearFreeSelectedProduct() {
    this.#hasFreeTankSelected = false;
    this.#freeVid = 0;
    this.classList.remove('dr-bundle--has-free-product');
  }

  /** @returns {void} */
  #syncPrimarySelectedRows() {
    const v = this.#variant();
    if (!v) return;

    const productTitle = (this.dataset.productTitle || '').trim();
    const label = this.#variantDisplayLabel(v, productTitle);

    let src = (v.image || '').trim();
    if (src.startsWith('//')) src = `https:${src}`;

    this.querySelectorAll('.dr-bundle__selected-product-row--primary').forEach((row) => {
      const img = /** @type {HTMLImageElement | null} */ (
        row.querySelector('.dr-bundle__selected-img, .dr-bundle__selected-product-image')
      );
      const title = /** @type {HTMLElement | null} */ (
        row.querySelector('.dr-bundle__selected-title, .dr-bundle__selected-product-title')
      );
      if (img instanceof HTMLImageElement && src) {
        img.src = src;
        img.alt = productTitle || label || '';
      }
      if (title) title.textContent = label;
    });
  }

  /** @returns {void} */
  #syncSelectedRows() {
    this.#syncPrimarySelectedRows();

    const secondRow = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-second-selected-row]'));
    const secondImg = /** @type {HTMLImageElement | null} */ (this.querySelector('.dr-bundle__second-selected-image'));
    const secondTitle = /** @type {HTMLElement | null} */ (this.querySelector('.dr-bundle__second-selected-title'));
    const removeBtn = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-remove-second-product]'));
    if (!secondRow || !secondImg || !secondTitle) return;

    const shouldShow =
      this.#mode === 'double' &&
      this.#usesMixedDouble() &&
      this.#hasSecondProductSelected;

    this.classList.toggle('dr-bundle--has-second-product', shouldShow);
    if (removeBtn) {
      if (shouldShow) removeBtn.removeAttribute('hidden');
      else removeBtn.setAttribute('hidden', '');
    }
    if (shouldShow) {
      const pick = this.#cfg?.secondaryPicks?.[this.#secondaryPickIx];
      const sv = this.#secondChosenVariant();
      if (pick && sv) {
        secondTitle.textContent = this.#variantDisplayLabel(sv, (pick.title || '').trim());

        let src = (sv.image || pick.image || '').trim();
        if (src.startsWith('//')) src = `https:${src}`;
        if (src) {
          secondImg.src = src;
          secondImg.alt = (pick.title || '').trim();
        }
      }
    }

    const freeRow = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-free-selected-row]'));
    const freeImg = this.querySelector('.dr-bundle__free-selected-image');
    const freeTitle = /** @type {HTMLElement | null} */ (this.querySelector('.dr-bundle__free-selected-title'));
    const shouldShowFree =
      this.#mode === 'double' &&
      this.#usesMixedDouble() &&
      this.#hasFreeTankSelected;

    this.classList.toggle('dr-bundle--has-free-product', shouldShowFree);
    if (freeRow) {
      if (shouldShowFree) freeRow.removeAttribute('hidden');
      else freeRow.setAttribute('hidden', '');
    }
    if (!freeRow || !freeImg || !freeTitle || !shouldShowFree) return;

    const freePick = this.#giftPick();
    const fv = this.#freeChosenVariant();
    if (!freePick || !fv) return;

    freeTitle.textContent = this.#giftDisplayLabel(freePick, fv);

    let freeSrc = (fv.image && !/no-image/i.test(fv.image) ? fv.image : '').trim();
    if (!freeSrc) freeSrc = (freePick.image || '').trim();
    freeSrc = this.#normalizeImageUrl(freeSrc);

    if (freeSrc) {
      this.#logFreeTankSelection(freePick, fv, freeSrc);
      this.#setImageSource(freeImg, freeSrc, (freePick.title || '').trim());
    }
  }

  /** @returns {string} */
  #selectedSecondColorLabel() {
    return this.#colorLabelForPick(this.#secondaryPickIx);
  }

  /**
   * @param {number} pickIx
   * @returns {string}
   */
  #colorLabelForPick(pickIx) {
    const pick = this.#cfg?.secondaryPicks?.[pickIx];
    if (!pick) return '';

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('.dr-bundle__pick-radio'));
    let domPickIndex = pickIx;
    let arrayIx = 0;
    for (const radio of radios) {
      const parsed = Number.parseInt(String(radio.value), 10);
      if (Number.isNaN(parsed)) continue;
      if (arrayIx === pickIx) {
        domPickIndex = parsed;
        break;
      }
      arrayIx += 1;
    }

    const select = /** @type {HTMLSelectElement | null} */ (
      this.querySelector(`select.dr-bundle__secondary-variant-select[data-dr-pick-index="${domPickIndex}"]`)
    );
    const vid = select?.value ? Number.parseInt(String(select.value), 10) : 0;
    const sv = pick.variants.find((x) => Number(x.id) === Number(vid)) ?? pick.variants.find((x) => x.available) ?? pick.variants[0];

    const variantColor = this.#normalizedLabel(sv?.color);
    if (variantColor) return variantColor;

    const productColor = this.#normalizedLabel(pick?.productColor);
    if (productColor) return productColor;

    const label = (select?.selectedOptions?.[0]?.textContent || '').trim();
    if (!label || /default title/i.test(label)) return '';
    const cleaned = label.replace(/\s*\([^)]*\)\s*$/g, '').trim();
    if (!cleaned) return '';
    const colorPart = cleaned.split('/')[0]?.trim() || cleaned;
    return /default title/i.test(colorPart) ? '' : colorPart;
  }

  /** @returns {void} */
  #syncModalProductTitles() {
    this.querySelectorAll('[data-dr-modal-item]').forEach((item) => {
      const el = /** @type {HTMLElement} */ (item);
      const domPickIndex = Number.parseInt(String(el.dataset.pickIndex || '0'), 10) || 0;
      const arrayIx = this.#secondaryPickIndexFromDom(domPickIndex);
      const pick = this.#cfg?.secondaryPicks?.[arrayIx];
      if (!pick) return;
      const titleEl = /** @type {HTMLElement | null} */ (el.querySelector('.dr-bundle-modal__product-title'));
      if (!titleEl) return;
      const baseTitle = (pick.title || '').trim();
      const color = this.#colorLabelForPick(arrayIx);
      titleEl.textContent = baseTitle && color ? `${baseTitle} - ${color}` : baseTitle || color || '';
    });
  }

  /** @returns {void} */
  #primeModalForTarget() {
    this.classList.toggle('dr-bundle--picking-free', this.#pickerTarget === 'free');

    /** @type {HTMLElement | null} */
    const secondPicks = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-secondary-picks]'));
    /** @type {HTMLElement | null} */
    const freePicks = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-free-picks]'));
    if (secondPicks) secondPicks.hidden = this.#pickerTarget === 'free';
    if (freePicks) freePicks.hidden = this.#pickerTarget !== 'free';

    if (this.#pickerTarget === 'free') {
      const sel = /** @type {HTMLSelectElement | null} */ (
        this.querySelector(`select[data-dr-free-variant-select][data-dr-free-pick-index="${this.#freePickIx}"]`)
      );
      const fv = this.#freeChosenVariant();
      if (sel && fv?.id) sel.value = String(fv.id);
    }

    const pickIx = this.#pickerTarget === 'free' ? this.#freePickIx : this.#secondaryPickIx;
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('.dr-bundle__pick-radio'));
    let targetDomIx = 0;
    let arrayIx = 0;
    for (const radio of radios) {
      const parsed = Number.parseInt(String(radio.value), 10);
      if (Number.isNaN(parsed)) continue;
      if (arrayIx === pickIx) {
        targetDomIx = parsed;
        break;
      }
      arrayIx += 1;
    }

    radios.forEach((radio) => {
      radio.checked = Number.parseInt(String(radio.value), 10) === targetDomIx;
    });

    const dialogTitle = /** @type {HTMLElement | null} */ (this.querySelector('[id^="dr-second-dialog-title-"]'));
    if (dialogTitle) dialogTitle.textContent = this.#pickerTarget === 'free' ? 'Choose your FREE tank 🎁' : 'Choose your second tank';

    const freeSubtitle = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-free-modal-subtitle]'));
    if (freeSubtitle) freeSubtitle.hidden = this.#pickerTarget !== 'free';
  }

  #syncFromRadios() {
    /** @type {HTMLInputElement | null} */
    let r = /** @type {HTMLInputElement | null} */ (this.querySelector('.dr-bundle__radios:checked:not(:disabled)'));
    if (!r) {
      r = /** @type {HTMLInputElement | null} */ (this.querySelector('.dr-bundle__radios[value="double"]'));
      if (r) r.checked = true;
    }
    this.#mode = r?.value === 'single' ? 'single' : 'double';

    this.querySelectorAll('.js-dr-opt').forEach((el) => {
      const rawMode = /** @type {HTMLElement} */ (el).dataset.mode || '';
      if (rawMode === 'soldout') {
        el.classList.remove('dr-bundle__option--selected');
        return;
      }
      const mode = rawMode === 'single' ? 'single' : 'double';
      el.classList.toggle('dr-bundle__option--selected', mode === this.#mode);
    });

    /** @type {HTMLElement | null} */
    const gifts = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-gifts]'));
    const giftsEnabled = String(this.dataset.enableGifts || '') === 'true';
    gifts?.classList.toggle('dr-bundle__gifts--hidden', !giftsEnabled);
    gifts?.classList.toggle('dr-bundle__gifts--locked', giftsEnabled && this.#mode === 'single');
    const giftsState = giftsEnabled ? (this.#mode === 'single' ? 'locked' : 'unlocked') : 'hidden';
    const shouldAnimateUnlockedMedia =
      gifts &&
      giftsState === 'unlocked' &&
      this.#lastGiftsState !== giftsState &&
      this.#lastGiftsState !== 'hidden';

    if (shouldAnimateUnlockedMedia) {
      gifts.classList.remove('dr-bundle__gifts--animating');
      void gifts.offsetWidth;
      gifts.classList.add('dr-bundle__gifts--animating');
      window.setTimeout(() => gifts.classList.remove('dr-bundle__gifts--animating'), 700);
    }
    this.#lastGiftsState = giftsState;

    if (this.#mode !== 'double') {
      this.#clearSecondSelectedProduct();
      this.#clearFreeSelectedProduct();
    }

    this.#syncSecondPickFromDom();
    this.#paint();
    this.#syncWithProductForm();
  }

  #paint() {
    const v = this.#variant();
    if (!v) return;

    this.#syncSecondPickFromDom();

    const pct = Number(this.#requireCfg().discountSecondPercent ?? this.dataset.discountPct ?? 30) || 30;
    const p = Number(v.price) || 0;

    /** Single card */
    /** @type {HTMLElement | null} */
    const sm = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="single-main"]'));
    if (sm) sm.textContent = this.#money(p);

    this.#setCompare('[data-dr-role="single-compare"]', v);

    const mixed = this.#usesMixedDouble();
    /** @type {DrVariant | undefined} */
    const sv = mixed && this.#hasSecondProductSelected ? this.#secondChosenVariant() : undefined;
    /** @type {DrVariant | undefined} */
    const fv = mixed && this.#hasFreeTankSelected ? this.#freeChosenVariant() : undefined;

    /** @type {number} */
    let totalD;
    /** @type {number} */
    let refPairBoth;

    if (mixed && sv) {
      const p2 = Number(sv.price) || 0;
      totalD = p + this.#disc(p2, pct);
      refPairBoth = this.#refCents(v) + this.#refCents(sv) + this.#refCents(fv || v);
    } else {
      totalD = p + this.#disc(p, pct);
      refPairBoth = this.#refCents(v) + this.#refCents(v) + this.#refCents(v);
    }

    const eachD = Math.round(totalD / 2);

    /** @type {HTMLElement | null} */
    const dt = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="double-total"]'));
    if (dt) dt.textContent = this.#money(totalD);

    /** @type {HTMLElement | null} */
    const de = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="double-each"]'));
    if (de) de.textContent = this.#money(eachD);

    const freeRef = this.#refCents(fv || v);
    /** @type {HTMLElement | null} */
    const freeCompare = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="free-compare"]'));
    if (freeCompare) freeCompare.textContent = this.#money(freeRef);
    /** @type {HTMLElement | null} */
    const freeModalCompare = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="free-modal-compare"]'));
    if (freeModalCompare) freeModalCompare.textContent = this.#money(freeRef);
    /** @type {HTMLElement | null} */
    const freeSelectedCompare = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="free-selected-compare"]'));
    if (freeSelectedCompare) freeSelectedCompare.textContent = this.#money(freeRef);
    /** @type {HTMLElement | null} */
    const giftFreeCompare = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="gift-free-tank-compare"]'));
    if (giftFreeCompare) giftFreeCompare.textContent = this.#money(freeRef);
    /** @type {HTMLElement | null} */
    const soldTotal = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="soldout-total"]'));
    if (soldTotal) soldTotal.textContent = this.#money(p * 3);
    /** @type {HTMLElement | null} */
    const soldCompare = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="soldout-compare"]'));
    if (soldCompare) soldCompare.textContent = this.#money(this.#refCents(v) * 6);
    /** @type {HTMLElement | null} */
    const soldEach = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-money="soldout-each"]'));
    if (soldEach) soldEach.textContent = this.#money(p);

    /** @type {HTMLElement | null} */
    const dcEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-role="double-compare"]'));
    if (dcEl) {
      if (refPairBoth > totalD) {
        dcEl.textContent = this.#money(refPairBoth);
        dcEl.classList.remove('is-empty');
        dcEl.dataset.empty = 'false';
      } else {
        dcEl.textContent = '';
        dcEl.classList.add('is-empty');
        dcEl.dataset.empty = 'true';
      }
    }

    /** @type {HTMLElement | null} */
    const pill = /** @type {HTMLElement | null} */ (this.querySelector('[data-dr-role="save-pill"]'));
    if (pill) {
      const saved = refPairBoth - totalD;
      if (this.#mode === 'double' && saved > 0) {
        pill.hidden = false;
        pill.textContent = `Save ${this.#money(saved)}`;
      } else {
        pill.hidden = true;
        pill.textContent = '';
      }
    }

    this.#syncSelectedRows();
    this.#syncModalProductTitles();
    this.#syncBuyButtonPriceMarkups(v, p, totalD, refPairBoth);
  }

  /**
   * Prijs op de hoofd-ATC-knop + sticky bar (Horizon: statische Liquid); meesturen met 1- vs 2-pack-totaal.
   *
   * @param {DrVariant} v
   * @param {number} p unit price cents (eerste masker)
   * @param {number} totalD 2-pack totaal cents
   * @param {number} refPairBoth som referentieprijzen beide stuks
   */
  #syncBuyButtonPriceMarkups(v, p, totalD, refPairBoth) {
    if (String(this.dataset.syncBuyButtonPrice ?? 'true') !== 'true') return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    /** @type {NodeListOf<HTMLElement>} */
    const wraps = /** @type {any} */ (
      section.querySelectorAll('[data-testid="buy-button-price-display"], [data-testid="sticky-price-display"]')
    );
    if (!wraps.length) return;

    /** @type {number} */
    let mainCents;
    /** @type {number | null} */
    let compareCents = null;

    if (this.#mode === 'single') {
      mainCents = p;
      const c = v.compare_at_price != null ? Number(v.compare_at_price) : null;
      if (c != null && c > p) compareCents = c;
    } else {
      mainCents = totalD;
      if (refPairBoth > totalD) compareCents = refPairBoth;
    }

    const html = this.#buildBuyButtonPriceHtml(mainCents, compareCents);

    wraps.forEach((wrap) => {
      if (wrap.querySelector('.price-range, .volume-pricing-note')) return;
      wrap.innerHTML = html;
    });
  }

  /**
   * Markup gelijk aan `snippets/price.liquid` met `show_sale_price_first: true` (compact in knop).
   *
   * @param {number} mainCents
   * @param {number | null} compareCents
   * @returns {string}
   */
  #buildBuyButtonPriceHtml(mainCents, compareCents) {
    const saleLabel = this.#escapeHtml(String(this.dataset.i18nPriceSale || 'Sale price'));
    const regularLabel = this.#escapeHtml(String(this.dataset.i18nPriceRegular || 'Regular price'));
    const main = this.#escapeHtml(this.#money(mainCents));

    if (compareCents != null && compareCents > mainCents) {
      const cmp = this.#escapeHtml(this.#money(compareCents));
      return `<span ref="priceContainer"><span role="group"><span class="visually-hidden">${saleLabel}&nbsp;</span><span class="price">${main}</span></span><span role="group"><span class="visually-hidden">${regularLabel}&nbsp;</span><span class="compare-at-price">${cmp}</span></span></span>`;
    }

    return `<span ref="priceContainer"><span class="price">${main}</span></span>`;
  }

  /**
   * @param {string} s
   * @returns {string}
   */
  #escapeHtml(s) {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {string} sel
   * @param {DrVariant} v
   */
  #setCompare(sel, v) {
    const el = /** @type {HTMLElement | null} */ (this.querySelector(sel));
    if (!el) return;
    const p = Number(v.price) || 0;
    const c = v.compare_at_price != null ? Number(v.compare_at_price) : null;
    if (c != null && c > p) {
      el.textContent = this.#money(c);
      el.classList.remove('is-empty');
      el.dataset.empty = 'false';
    } else {
      el.textContent = '';
      el.classList.add('is-empty');
      el.dataset.empty = 'true';
    }
  }
}

if (!customElements.get('dore-bundle-block')) {
  customElements.define('dore-bundle-block', DoreBundleBlock);
}
